import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useNode, useEditor } from '@craftjs/core';

import PaddingStyle from '@resolvers/tools/PaddingStyle/PaddingStyle';
import SizeStyle from '@resolvers/tools/SizeStyle/SizeStyle';
import AlignStyle from '@resolvers/tools/AlignStyle/AlignStyle';

import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import { EDITOR_RESOLVERS } from '@common/constants';
import MyButton from '@common/components/Button/MyButton';
import {
  findStringIndex,
  filterNodesByParentId,
  getListOfNodes,
} from '@common/helpers/helpers';
import { useBorder } from '@common/providers/BorderProvider/BorderProvider';

const TableCell = ({
  children,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  align,
  width,
  elementProps,
  borderAllShow,
  borderAllSize,
  borderAllType,
  borderAllColor,
}) => {
  const { actions } = useEditor();
  const {
    connectors: { connect },
    actions: { setCustom },
  } = useNode();

  const { t } = useTranslation();
  const ref = useRef(null);

  useEffect(() => {
    if (ref?.current && connect) {
      connect(ref.current);
    }
  }, [connect]);

  const checkTableNameByProps = (elementProps) => {
    if (elementProps?.isHeader) return t('table_cell_header');
    if (elementProps?.isFooter) return t('table_cell_footer');
    return t('table_cell');
  };

  useEffect(() => {
    actions.history.ignore(() => {
      setTimeout(() => {
        setCustom((props) => {
          props.displayName = checkTableNameByProps(elementProps);
        });
      }, 0);
    });
  }, [setCustom, t]);

  return (
    <td
      ref={ref}
      style={{
        fontSize: 'inherit',
        paddingLeft: `${paddingLeft || 0}px`,
        paddingRight: `${paddingRight || 0}px`,
        paddingTop: `${paddingTop || 0}px`,
        paddingBottom: `${paddingBottom || 0}px`,
        overflow: 'hidden',
        width: `${width || 0}%`,
        border:
          borderAllShow &&
          `${borderAllSize}px ${borderAllType} ${borderAllColor}`,
      }}
      align={align}
    >
      {children || <p />}
    </td>
  );
};

const hAlignOptions = [
  {
    name: 'left',
    iconName: 'left',
    iconSize: 'small',
  },
  {
    name: 'right',
    iconName: 'right',
    iconSize: 'small',
  },
];

const MIN_NUMBER_OF_COLUMNS = 1;

const TableCellConfigPanel = () => {
  const { t } = useTranslation();

  const { cellsForDelete, setCellsForDelete } = useBorder();

  const {
    id: tableCellId,
    parentId,
    isFooter,
    isHeader,
    isFixed,
  } = useNode(({ data }) => {
    return {
      parentId: data.parent,
      isFooter: data?.props?.elementProps?.isFooter,
      isHeader: data?.props?.elementProps?.isHeader,
      isFixed: data?.props?.elementProps?.isFixed,
    };
  });

  const { query, actions } = useEditor();

  const allNodes = getListOfNodes(query);
  const parentRow = allNodes.find((node) => node.id === parentId);
  const tableEl =
    parentRow?.data?.parent &&
    allNodes.find((node) => node.id === parentRow?.data?.parent);

  const onDelete = () => {
    if (cellsForDelete?.length === 3) {
      cellsForDelete?.forEach((cellId) => actions.delete(cellId));
      setCellsForDelete();
    }
  };

  const onMouseDeleteBtnEnter = () => {
    if (
      parentRow?.id &&
      parentRow?.data?.nodes?.length > MIN_NUMBER_OF_COLUMNS
    ) {
      const tableNodes = allNodes.filter(
        filterNodesByParentId(parentRow.data.parent)
      );

      const deletedCellIndex = findStringIndex(
        parentRow?.data?.nodes,
        tableCellId
      );
      let idsForDeletion = [];
      tableNodes.forEach((rowNode) => {
        const columnCellIdForDelete = rowNode.data.nodes[deletedCellIndex];
        idsForDeletion.push(columnCellIdForDelete);
      });

      setCellsForDelete(idsForDeletion);
    }
  };

  const onMouseDeleteBtnLeave = () => {
    setCellsForDelete();
  };

  return (
    <SettingsWrapper>
      <SettingsRow sx={{ mb: 2 }}>
        <AlignStyle propKey='align' options={hAlignOptions} />
      </SettingsRow>
      <SettingsRow>
        <PaddingStyle />
      </SettingsRow>
      {(isHeader || !(isFooter || tableEl?.data?.props?.showHeader)) && (
        <SettingsRow>
          <SizeStyle propKey='width' />
        </SettingsRow>
      )}

      <SettingsRow sx={{ mt: 3 }}>
        <MyButton
          color='error'
          onClick={onDelete}
          onMouseEnter={onMouseDeleteBtnEnter}
          onMouseLeave={onMouseDeleteBtnLeave}
          disabled={isFixed}
        >
          {t('delete_cell_btn')}
        </MyButton>
      </SettingsRow>
    </SettingsWrapper>
  );
};

export const TableCellDefaultProps = {
  children: null,
  paddingLeft: '10',
  paddingRight: '10',
  paddingTop: '10',
  paddingBottom: '10',
  width: '10',
  elementProps: {},
  align: 'left',
  borderAllShow: true,
  borderAllSize: '1',
  borderAllType: 'solid',
  borderAllColor: '#F6EDED',
  isDeletable: false,
};

TableCell.defaultProps = TableCellDefaultProps;

TableCell.propTypes = {
  children: PropTypes.node,
  paddingLeft: PropTypes.string,
  paddingRight: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingBottom: PropTypes.string,
  showBorder: PropTypes.bool,
  width: PropTypes.string,
  align: PropTypes.string,
};

TableCell.craft = {
  displayName: EDITOR_RESOLVERS.COMMON.TABLE_CELL,
  name: EDITOR_RESOLVERS.COMMON.TABLE_CELL,
  props: TableCellDefaultProps,
  related: { settings: TableCellConfigPanel },
  rules: {
    canMoveIn: (incoming, self, helper) => {
      return !(
        self.data.props.elementProps.isFixed &&
        incoming[0]?.data?.name === 'Placeholder'
      );
    },
  },
};

export default TableCell;
